import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { comoErroDaApi, ErroDaApi } from '../../../shared/api/erros';
import { opcoesDasOperadoras } from '../../../shared/operadoras/operadoras';
import { cadastrarContrato, chavesDeContratos } from '../api/contratos';
import { ROTULOS_DE_ENTIDADE, UFS, type Contrato, type Entidade } from '../api/tipos';

const ENTIDADES: readonly Entidade[] = ['operadora_de_planos', 'saude'];

/**
 * Cadastro de contrato (operadora, entidade, UF): a porta de entrada de qualquer operadora.
 * Antes dele, só existiam os contratos do seed de desenvolvimento, e uma operadora nova não
 * tinha onde receber o PDF. A operadora vem do cadastro (`GET /operadoras`); o servidor recusa
 * UF fora das 27 e contrato repetido para a mesma (operadora, entidade, UF).
 */
export function CadastroDeContrato({
  aoCadastrar,
}: {
  aoCadastrar: (contrato: Contrato) => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [aberto, definirAberto] = useState(false);
  const consultaDasOperadoras = useQuery({ ...opcoesDasOperadoras(), enabled: aberto });
  const [operadoraId, definirOperadoraId] = useState('');
  const [entidade, definirEntidade] = useState<Entidade>('operadora_de_planos');
  const [uf, definirUf] = useState('');
  const [nome, definirNome] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const faltando = [
    operadoraId === '' ? 'a operadora' : null,
    uf === '' ? 'a UF' : null,
    nome.trim() === '' ? 'o nome' : null,
  ].filter((item): item is string => item !== null);

  const limpar = (): void => {
    definirOperadoraId('');
    definirEntidade('operadora_de_planos');
    definirUf('');
    definirNome('');
    definirErro(null);
  };

  const cadastrar = (): void => {
    if (faltando.length > 0 || enviando) return;
    definirEnviando(true);
    definirErro(null);
    cadastrarContrato({ operadoraId, entidade, uf, nome })
      .then(async (contrato) => {
        await clienteDeConsultas.invalidateQueries({ queryKey: chavesDeContratos.lista() });
        limpar();
        definirAberto(false);
        aoCadastrar(contrato);
      })
      .catch((causa: unknown) => {
        if (causa instanceof ErroDaApi && causa.status === 401) return;
        definirErro(comoErroDaApi(causa).message);
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  if (!aberto) {
    return (
      <Button
        variant="outlined"
        onClick={() => {
          definirAberto(true);
        }}
        sx={{ alignSelf: 'flex-start' }}
      >
        Novo contrato
      </Button>
    );
  }

  return (
    <Stack
      spacing={1.5}
      component="form"
      aria-label="Cadastrar contrato"
      onSubmit={(evento) => {
        evento.preventDefault();
        cadastrar();
      }}
    >
      <Typography variant="h3">Novo contrato</Typography>
      <Typography variant="body2" color="text.secondary">
        Um contrato é a operadora, a entidade e a UF. Depois de cadastrado, ele aparece no seletor acima e já recebe
        o PDF.
      </Typography>

      {consultaDasOperadoras.isError ? (
        <Alert severity="error" role="alert">
          Não foi possível carregar as operadoras: {comoErroDaApi(consultaDasOperadoras.error).message}
        </Alert>
      ) : null}

      <TextField
        select
        label="Operadora"
        size="small"
        value={operadoraId}
        onChange={(evento) => {
          definirOperadoraId(evento.target.value);
        }}
        disabled={!consultaDasOperadoras.isSuccess}
        sx={{ maxWidth: 360 }}
      >
        {(consultaDasOperadoras.data ?? []).map((operadora) => (
          <MenuItem key={operadora.id} value={operadora.id}>
            {operadora.nome}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          select
          label="Entidade"
          size="small"
          value={entidade}
          onChange={(evento) => {
            definirEntidade(evento.target.value as Entidade);
          }}
          sx={{ minWidth: 220 }}
        >
          {ENTIDADES.map((valor) => (
            <MenuItem key={valor} value={valor}>
              {ROTULOS_DE_ENTIDADE[valor]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="UF"
          size="small"
          value={uf}
          onChange={(evento) => {
            definirUf(evento.target.value);
          }}
          sx={{ minWidth: 100 }}
        >
          {UFS.map((sigla) => (
            <MenuItem key={sigla} value={sigla}>
              {sigla}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <TextField
        label="Nome do contrato"
        size="small"
        value={nome}
        onChange={(evento) => {
          definirNome(evento.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 120 } }}
        sx={{ maxWidth: 360 }}
      />

      {erro !== null ? (
        <Alert severity="error" role="alert">
          {erro}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button type="submit" variant="contained" disabled={faltando.length > 0 || enviando}>
          {enviando ? 'Cadastrando…' : 'Cadastrar'}
        </Button>
        <Button
          onClick={() => {
            limpar();
            definirAberto(false);
          }}
        >
          Cancelar
        </Button>
        {faltando.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            Para cadastrar, falta escolher {faltando.join(', ')}.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}
